import React, { lazy, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import PropTypes from 'prop-types';

import ErrorLayout from '../layouts/ErrorLayout';
import MainLayout from '../layouts/MainLayout';

import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';
import Internal404 from 'components/errors/Internal404';
import NotFoundRedirect from 'components/errors/NotFoundRedirect';

import { Welcome } from 'pages/Welcome';

import { selectAllLocations } from 'store/locations';

// Route-level code-splitting: each page is its own chunk, fetched on first visit (MainLayout's
// Suspense shows a spinner in the content area meanwhile). Two rules keep the splitting real:
// import the DEEPEST per-page path — 'pages' (and the mid-level folder barrels) re-export
// everything, so importing them pulls every page into one chunk — and map the pages' named
// exports onto the default export React.lazy expects.
// Chunk files are content-hashed and the old ones disappear from the Static Web App on every
// deploy, so a browser session opened before a deploy hits a ChunkLoadError the first time it
// navigates to a page whose chunk it hasn't downloaded yet — which rendered as a blank content
// area. Reload once to pick up the fresh index.html (and its new chunk manifest); the
// sessionStorage timestamp latch stops a reload loop when a chunk is genuinely unloadable.
const importWithReload = importer => () =>
  importer().catch(error => {
    const key = 'chunk-reload-at';
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
    throw error;
  });

const lazyNamed = (importer, name) =>
  lazy(() => importWithReload(importer)().then(m => ({ default: m[name] })));

const Location = lazy(
  importWithReload(() => import('pages/Home/Locations/Location'))
);
const Overview = lazy(
  importWithReload(() => import('pages/Home/Locations/Overview'))
);
const CustomerDetails = lazyNamed(
  () => import('shared/components/CustomerDetails'),
  'CustomerDetails'
);

const Accounting = lazyNamed(
  () => import('pages/Accounting/Overview'),
  'Accounting'
);
const AddContract = lazyNamed(
  () => import('pages/Customers/AddContract'),
  'AddContract'
);
const AddMiner = lazyNamed(() => import('pages/Hardware/AddMiner'), 'AddMiner');
const AddProduct = lazyNamed(
  () => import('pages/Products/BuyAndHost/AddProduct'),
  'AddProduct'
);
const AllMiners = lazyNamed(
  () => import('pages/Hardware/AllMiners'),
  'AllMiners'
);
const AllTransactions = lazyNamed(
  () => import('pages/Accounting/AllTransactions'),
  'AllTransactions'
);
const Birthdays = lazyNamed(
  () => import('pages/Marketing/Birthdays'),
  'Birthdays'
);
const BrandingSettings = lazyNamed(
  () => import('pages/Settings/Branding'),
  'BrandingSettings'
);
const BuyMarketplace = lazyNamed(
  () => import('pages/Connect/BuyMarketplace'),
  'BuyMarketplace'
);
const Capacity = lazyNamed(
  () => import('pages/Products/Hashpower/Capacity'),
  'Capacity'
);
const ForemanStatusOverview = lazyNamed(
  () => import('pages/Colo/Foreman'),
  'ForemanStatusOverview'
);
const CollectorsOverview = lazyNamed(
  () => import('pages/Colo/Collectors'),
  'CollectorsOverview'
);
const ConnectOverview = lazyNamed(
  () => import('pages/Connect/ConnectOverview'),
  'ConnectOverview'
);
const Consumption = lazyNamed(
  () => import('pages/Downloads/Consumption'),
  'Consumption'
);
const ConsumptionLocation = lazyNamed(
  () => import('pages/Downloads/Consumption'),
  'ConsumptionLocation'
);
const Contract = lazyNamed(() => import('pages/Contract'), 'Contract');
const Contracts = lazyNamed(
  () => import('pages/Customers/Contracts'),
  'Contracts'
);
const Customers = lazyNamed(
  () => import('pages/Customers/Customers'),
  'Customers'
);
const Dashboard = lazyNamed(() => import('pages/Dashboard'), 'Dashboard');
const DownloadContracts = lazyNamed(
  () => import('pages/Downloads/DownloadContracts'),
  'DownloadContracts'
);
const DownloadOrders = lazyNamed(
  () => import('pages/Downloads/DownloadOrders'),
  'DownloadOrders'
);
const FeeOverview = lazyNamed(
  () => import('pages/DuckEnergyFees/FeeOverview'),
  'FeeOverview'
);
const FeeTransactions = lazyNamed(
  () => import('pages/DuckEnergyFees/FeeTransactions'),
  'FeeTransactions'
);
const FlagsSettings = lazyNamed(
  () => import('pages/Colo/Flags'),
  'FlagsSettings'
);
const GoogleAnalytics = lazyNamed(
  () => import('pages/Marketing/GoogleAnalytics'),
  'GoogleAnalytics'
);
const HardwareOverview = lazyNamed(
  () => import('pages/Hardware/HardwareOverview'),
  'HardwareOverview'
);
const HashpowerOrders = lazyNamed(
  () => import('pages/Products/Hashpower/HashpowerOrders'),
  'HashpowerOrders'
);
const HelpDesk = lazyNamed(() => import('pages/Tasks/HelpDesk'), 'HelpDesk');
const Inventory = lazyNamed(
  () => import('pages/Products/BuyAndHost/Inventory'),
  'Inventory'
);
const Invoices = lazyNamed(
  () => import('pages/Downloads/Invoices'),
  'Invoices'
);
const LandingPage = lazyNamed(
  () => import('pages/Marketing/LandingPage'),
  'LandingPage'
);
const MailCampaigns = lazyNamed(
  () => import('pages/Marketing/MailCampaigns'),
  'MailCampaigns'
);
const MinerDetails = lazyNamed(
  () => import('pages/MinerDetails'),
  'MinerDetails'
);
const Miners = lazyNamed(() => import('pages/Tasks/Miners'), 'Miners');
const Monitoring = lazyNamed(
  () => import('pages/Settings/Monitoring'),
  'Monitoring'
);
const Orders = lazyNamed(
  () => import('pages/Products/BuyAndHost/Orders'),
  'Orders'
);
const OutletsOverview = lazyNamed(
  () => import('pages/Colo/Outlets'),
  'OutletsOverview'
);
const Parameters = lazyNamed(
  () => import('pages/Settings/Parameters'),
  'Parameters'
);
const PaymentProvidersOverview = lazyNamed(
  () => import('pages/PaymentProviders/PaymentProvidersOverview'),
  'PaymentProvidersOverview'
);
const PaymentRequests = lazyNamed(
  () => import('pages/Accounting/PaymentRequests'),
  'PaymentRequests'
);
const Pool = lazyNamed(() => import('pages/Pool/Pool'), 'Pool');
const PoolInvoice = lazyNamed(
  () => import('pages/Downloads/PoolInvoice'),
  'PoolInvoice'
);
const PoolOverview = lazyNamed(
  () => import('pages/Pool/PoolOverview'),
  'PoolOverview'
);
const PopUps = lazyNamed(() => import('pages/Marketing/PopUps'), 'PopUps');
const Prices = lazyNamed(
  () => import('pages/Products/Hashpower/Prices'),
  'Prices'
);
const PricesGroup = lazyNamed(() => import('pages/PricesGroup'), 'PricesGroup');
const ProductsSubscriptions = lazyNamed(
  () => import('pages/Products/BuyAndHost/ProductsSubscriptions'),
  'ProductsSubscriptions'
);
const Revenue = lazyNamed(() => import('pages/Downloads/Revenue'), 'Revenue');
const SelfMiningMiners = lazyNamed(
  () => import('pages/SelfMining/SelfMiningMiners'),
  'SelfMiningMiners'
);
const SelfMiningOverview = lazyNamed(
  () => import('pages/SelfMining/SelfMiningOverview'),
  'SelfMiningOverview'
);
const SelfMiningRevenue = lazyNamed(
  () => import('pages/SelfMining/SelfMiningRevenue'),
  'SelfMiningRevenue'
);
const SelfMiningTransactions = lazyNamed(
  () => import('pages/SelfMining/SelfMiningTransactions'),
  'SelfMiningTransactions'
);
const SellMarketplace = lazyNamed(
  () => import('pages/Connect/SellMarketplace'),
  'SellMarketplace'
);
const SimilarWeb = lazyNamed(
  () => import('pages/Marketing/SimilarWeb'),
  'SimilarWeb'
);
const SourcesOverview = lazyNamed(
  () => import('pages/Colo/Sources'),
  'SourcesOverview'
);
const Subscriptions = lazyNamed(
  () => import('pages/Downloads/Subscriptions'),
  'Subscriptions'
);
const Tickets = lazyNamed(() => import('pages/Tasks/Tickets'), 'Tickets');
const UptimeGuarantee = lazyNamed(
  () => import('pages/Products/BuyAndHost/UptimeGuarantee'),
  'UptimeGuarantee'
);
const UserAnalytics = lazyNamed(
  () => import('pages/Marketing/UserAnalytics'),
  'UserAnalytics'
);

import { useAuth0 } from '@auth0/auth0-react';
import ErrorBanner from 'components/errors/ErrorBanner';
import JwtPage from 'components/errors/JWTPage';
import NoPermissionPage from 'components/errors/NoPermissionPage';
import { usePermissions } from 'components/navbar/vertical/usePermissions';
import { ErrorsContext } from 'context/Context';
import { PagePermissionProvider } from 'context/PagePermissionContext';
import ProductProvider from 'pages/Products/BuyAndHost/Inventory/ProductProvider';
import { Col, Spinner } from 'react-bootstrap';
import { useFeatures } from 'hooks/useFeatures';
import { getDomainAndSubdomain } from 'shared/utils/getDomainAndSubdomain';
import { selectAllPools } from 'store/pool';

const ProtectedRoute = ({ pageKey, children, ...rest }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  const { elementsPermission } = usePermissions(pageKey);

  const { errors } = useContext(ErrorsContext);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <Col xs={12}>
        <Spinner className="position-absolute start-50" animation="grow" />
      </Col>
    );
  }

  return (
    <PagePermissionProvider permissions={elementsPermission}>
      {errors.length > 0 &&
        errors.map((err, index) => <ErrorBanner key={index} message={err} />)}
      {React.cloneElement(children, { ...rest, pageKey })}
    </PagePermissionProvider>
  );
};

ProtectedRoute.propTypes = {
  pageKey: PropTypes.string,
  children: PropTypes.node.isRequired
};

const FalconRoutes = () => {
  const locations = useSelector(selectAllLocations);
  const pools = useSelector(selectAllPools);
  const { subdomain } = getDomainAndSubdomain();
  // Plan 06 §Key contracts: mining surfaces stay default-visible (guard is `!== false`); colo
  // screens are opt-in (guard is `=== true`).
  const { miningEnabled, coloEnabled } = useFeatures();

  const isHelpDeskAvailable =
    subdomain === 'amityage' ||
    subdomain === 'pantheon' ||
    subdomain === 'pantheon-aus';

  // const tenants = useSelector(selectAllTenants);

  return (
    <ProductProvider>
      <Routes>
        <Route element={<ErrorLayout />}>
          <Route path="errors/404" element={<Error404 />} />
          <Route path="errors/500" element={<Error500 />} />
        </Route>
        {/* Authentication */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Welcome />} />
        </Route>

        {/* MainLayout */}
        <Route element={<MainLayout />}>
          {/* CUSTOM PAGES */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute pageKey="web.admin.home.dashboard">
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="locations/overview"
            element={
              <ProtectedRoute pageKey="web.admin.home.locations.overview">
                <Overview />
              </ProtectedRoute>
            }
          />

          {/* Locations */}
          {locations.map(location => (
            <Route
              key={location.id}
              path={`locations/${location.id}`}
              element={
                <ProtectedRoute pageKey="web.admin.home.locations.location">
                  <Location id={location.id} name={location.name} />
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="locations/*"
            element={<Navigate to="/dashboard" replace />}
          />

          <Route
            path="/accounting"
            element={
              <ProtectedRoute pageKey="web.admin.home.accounting.overview">
                <Accounting />
              </ProtectedRoute>
            }
          />

          <Route
            path="/all-transactions"
            element={
              <ProtectedRoute pageKey="web.admin.home.accounting.allTransactions">
                <AllTransactions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-requests"
            element={
              <ProtectedRoute pageKey="web.admin.home.accounting.paymentRequests">
                <PaymentRequests />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fee/overview"
            element={
              <ProtectedRoute pageKey="web.admin.home.feeOverview">
                <FeeOverview />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fee/transactions"
            element={
              <ProtectedRoute pageKey="web.admin.home.feeTransactions">
                <FeeTransactions />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="/locations/add-location"
            element={
              <ProtectedRoute pageKey="web.admin.home.locations.add">
                <AddLocation />
              </ProtectedRoute>
            }
          /> */}

          {/* Tenants */}
          {/* <Route
            path="/tenants/overview"
            element={
              <ProtectedRoute pageKey="web.admin.tenants.overview">
                <TenantsOverview />
              </ProtectedRoute>
            }
          />
          {tenants?.map(tenant => {
            return (
              <Route
                key={tenant.id}
                path={`/dashboard/${tenant.id}`}
                element={
                  <ProtectedRoute pageKey="web.admin.home.dashboard">
                    <Dashboard tenant={tenant} />
                  </ProtectedRoute>
                }
              />
            );
          })}
          <Route
            path="tenants/*"
            element={<Navigate to="/dashboard" replace />}
          />

          <Route
            path="/tenants/onboard"
            element={
              <ProtectedRoute pageKey="web.admin.tenants.onboard">
                <Onboard />
              </ProtectedRoute>
            }
          /> */}

          {/* <Route
            path="/duckenergy-box/overview"
            element={
              <ProtectedRoute pageKey="web.admin.home.duckenergyBox.overview">
                <DuckEnergyBoxOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/duckenergy-box/add"
            element={
              <ProtectedRoute pageKey="web.admin.home.duckenergyBox.add">
                <AddDuckEnergyBox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/duckenergy-box/request"
            element={
              <ProtectedRoute pageKey="web.admin.home.duckenergyBox.request">
                <RequestDuckEnergyBox />
              </ProtectedRoute>
            }
          /> */}

          {/* Tasks */}
          {!isHelpDeskAvailable && (
            <Route
              path="/tasks/help-desk"
              element={
                <ProtectedRoute pageKey="web.admin.tasks.helpDesk">
                  <HelpDesk />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/tasks/customer-tasks"
            element={
              <ProtectedRoute pageKey="web.admin.tasks.customerTasks">
                <Tickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/miner-tasks"
            element={
              <ProtectedRoute pageKey="web.admin.tasks.minerTasks">
                <Miners />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/tasks/apdus"
            element={
              <ProtectedRoute pageKey="web.admin.tasks.apdus">
                <Apdus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/network"
            element={
              <ProtectedRoute pageKey="web.admin.tasks.network">
                <Network />
              </ProtectedRoute>
            }
          /> */}

          {/* Self Mining — hashrate/BTC dashboards; mining guard (Plan 06 §Key contracts) */}
          {miningEnabled && (
            <>
              <Route
                path="/self-mining/overview"
                element={
                  <ProtectedRoute pageKey="web.admin.selfMining.overview">
                    <SelfMiningOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/self-mining/revenue"
                element={
                  <ProtectedRoute pageKey="web.admin.selfMining.revenue">
                    <SelfMiningRevenue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/self-mining/miners"
                element={
                  <ProtectedRoute pageKey="web.admin.selfMining.miners">
                    <SelfMiningMiners />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/self-mining/transactions"
                element={
                  <ProtectedRoute pageKey="web.admin.selfMining.transactions">
                    <SelfMiningTransactions />
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* Customers */}
          <Route
            path="/customers/all"
            element={
              <ProtectedRoute pageKey="web.admin.customers.all">
                <Customers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts"
            element={
              <ProtectedRoute pageKey="web.admin.customers.contracts">
                <Contracts />
              </ProtectedRoute>
            }
          />

          <Route
            path="/add-contract"
            element={
              <ProtectedRoute pageKey="web.admin.customers.addContract">
                <AddContract />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contract/:id"
            element={
              <ProtectedRoute pageKey="web.admin.customers.contract">
                <Contract />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/customers/add"
            element={
              <ProtectedRoute pageKey="web.admin.customers.add">
                <AddCustomer />
              </ProtectedRoute>
            }
          /> */}

          {/* Hardware */}
          <Route
            path="/hardware/overview"
            element={
              <ProtectedRoute pageKey="web.admin.hardware.overview">
                <HardwareOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hardware/all-miners"
            element={
              <ProtectedRoute pageKey="web.admin.hardware.miners">
                <AllMiners />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hardware/add-miner"
            element={
              <ProtectedRoute pageKey="web.admin.hardware.minersAdd">
                <AddMiner />
              </ProtectedRoute>
            }
          />
          {/*}
          <Route
            path="/hardware/all-apdus"
            element={
              <ProtectedRoute pageKey="web.admin.hardware.apdus">
                <AllAPDU />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hardware/add-apdu"
            element={
              <ProtectedRoute pageKey="web.admin.hardware.apdusAdd">
                <AddAPDU />
              </ProtectedRoute>
            }
          /> */}

          {/* Products */}
          {/* <Route
            path="/buy-host/hosting-prices"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.hostingPrices">
                <HostingPrices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-host/hosting-prices/group/:groupId"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.pricesGroup">
                <PricesGroup />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/ecommerce/add-product"
            element={
              <ProtectedRoute pageKey="web.admin.ecommerce.addProduct">
                <AddProduct />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ecommerce/inventory"
            element={
              <ProtectedRoute pageKey="web.admin.ecommerce.inventory">
                <Inventory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ecommerce/orders"
            element={
              <ProtectedRoute pageKey="web.admin.ecommerce.orders">
                <Orders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ecommerce/subscriptions"
            element={
              <ProtectedRoute pageKey="web.admin.ecommerce.subscriptions">
                <ProductsSubscriptions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ecommerce/uptime-guarantee"
            element={
              <ProtectedRoute pageKey="web.admin.ecommerce.uptimeGuarantee">
                <UptimeGuarantee />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/products/inventory/add-product"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.addProduct">
                <AddProduct />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/inventory/product-details/.productId"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.productDetails">
                <ProductDetails />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route
            path="/buy-host/orders"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.orders">
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-host/orders/new"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.newOrder">
                <NewOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-host/miner-return"
            element={
              <ProtectedRoute pageKey="web.admin.products.buyHost.minerReturn">
                <MinerReturn />
              </ProtectedRoute>
            }
          /> */}

          {/* Miner Hosting */}
          {/* route removed (2026-08 frontend audit): hosting-prices price-group backend does not exist */}
          {/* <Route
            path="/miner-hosting/types"
            element={
              <ProtectedRoute pageKey="web.admin.products.minerHosting.types">
                <ActiveMiners />
              </ProtectedRoute>
            }
          />
          <Route
            path="/miner-hosting/add-type"
            element={
              <ProtectedRoute pageKey="web.admin.products.minerHosting.addType">
                <AddHostedMinerType />
              </ProtectedRoute>
            }
          />
          <Route
            path="/miner-hosting/orders"
            element={
              <ProtectedRoute pageKey="web.admin.products.minerHosting.orders">
                <HostingOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/miner-hosting/miner-return"
            element={
              <ProtectedRoute pageKey="web.admin.products.minerHosting.minerReturn">
                <MinerHostingMinerReturn />
              </ProtectedRoute>
            }
          /> */}

          {/* Hashpower */}
          <Route
            path="/hashpower/orders"
            element={
              <ProtectedRoute pageKey="web.admin.products.hashpowe.orders">
                <HashpowerOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hashpower/prices"
            element={
              <ProtectedRoute pageKey="web.admin.products.hashpower.prices">
                <Prices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hashpower/capacity"
            element={
              <ProtectedRoute pageKey="web.admin.products.hashpower.capacity">
                <Capacity />
              </ProtectedRoute>
            }
          />

          {/* Connect */}
          <Route
            path="/connect/overview"
            element={
              <ProtectedRoute pageKey="web.admin.connect.overview">
                <ConnectOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connect/buy-marketplace"
            element={
              <ProtectedRoute pageKey="web.admin.connect.buyMarketplace">
                <BuyMarketplace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connect/sell-marketplace"
            element={
              <ProtectedRoute pageKey="web.admin.connect.sellMarketplace">
                <SellMarketplace />
              </ProtectedRoute>
            }
          />

          {/* Subscriptions */}
          {/* <Route
            path="/subscriptions/duckenergy-pro"
            element={
              <ProtectedRoute pageKey="web.admin.products.subscriptions.duckenergyPro">
                <DuckEnergyPro />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions/extra-insurance"
            element={
              <ProtectedRoute pageKey="web.admin.products.subscriptions.extraInsurance">
                <ExtraInsurance />
              </ProtectedRoute>
            }
          /> */}

          {/* Pool configuration nav — mining guard (Plan 06 §Key contracts) */}
          {miningEnabled && (
            <>
              <Route
                path="/pool/overview"
                element={
                  <ProtectedRoute pageKey="web.admin.home.pools.overview">
                    <PoolOverview />
                  </ProtectedRoute>
                }
              />
              {pools.map(({ id }) => (
                <Route
                  key={id}
                  path={`pool/${id}`}
                  element={
                    <ProtectedRoute pageKey="web.admin.home.pools.pool">
                      <Pool poolId={id} />
                    </ProtectedRoute>
                  }
                />
              ))}
            </>
          )}
          {/* <Route
            path="/pool/add"
            element={
              <ProtectedRoute pageKey="web.admin.home.pools.add">
                <PoolAdd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pool/transactions"
            element={
              <ProtectedRoute pageKey="web.admin.home.pools.transactions">
                <PoolTransactions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pool/sub-accounts"
            element={
              <ProtectedRoute pageKey="web.admin.home.pools.subAccounts">
                <PoolSubAccounts />
              </ProtectedRoute>
            }
          /> */}

          {/* Colo (Plan 06 Task 3). Flags is always reachable (it's how an admin turns colo ON
              in the first place — gating it behind coloEnabled would make that impossible); the
              other three screens only make sense once colo:true, so they're colo-guarded. */}
          <Route
            path="/colo/flags"
            element={
              <ProtectedRoute pageKey="web.admin.colo.flags">
                <FlagsSettings />
              </ProtectedRoute>
            }
          />
          {coloEnabled && (
            <>
              <Route
                path="/colo/sources"
                element={
                  <ProtectedRoute pageKey="web.admin.colo.sources">
                    <SourcesOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/colo/outlets"
                element={
                  <ProtectedRoute pageKey="web.admin.colo.outlets">
                    <OutletsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/colo/foreman"
                element={
                  <ProtectedRoute pageKey="web.admin.colo.sources">
                    <ForemanStatusOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/colo/collectors"
                element={
                  <ProtectedRoute pageKey="web.admin.colo.collectors">
                    <CollectorsOverview />
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* Marketing */}
          <Route
            path="/marketing/user-analytics"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.userAnalytics">
                <UserAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/birthdays"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.birthdays">
                <Birthdays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/mail-campaigns"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.mailCampaigns">
                <MailCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/newsletter"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.newsletter">
                <MailCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/pop-ups"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.popups">
                <PopUps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/landing-page"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.landingPage">
                <LandingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/google-analytics"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.googleAnalytics">
                <GoogleAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/similarweb"
            element={
              <ProtectedRoute pageKey="web.admin.marketing.similarweb">
                <SimilarWeb />
              </ProtectedRoute>
            }
          />

          {/* Downloads */}
          <Route
            path="/downloads/invoices"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.invoices">
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads/contracts"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.contracts">
                <DownloadContracts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads/total-consumptions/all-locations"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.consumption.allLocations">
                <Consumption />
              </ProtectedRoute>
            }
          />
          {(locations ?? []).map(location => (
            <Route
              key={`download-${location.id}`}
              path={`/downloads/total-consumptions/${location.id}`}
              element={
                <ProtectedRoute pageKey="web.admin.downloads.consumption.location">
                  <ConsumptionLocation
                    id={location.id}
                    locationName={location.name}
                  />
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="/downloads/pool"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.pool">
                <PoolInvoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads/subscriptions"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.subscriptions">
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads/revenue"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.revenue">
                <Revenue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads/orders"
            element={
              <ProtectedRoute pageKey="web.admin.downloads.orders">
                <DownloadOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customer-details/:id"
            element={
              <ProtectedRoute pageKey="web.admin.home.customerDetails">
                <CustomerDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/miner-details/:id"
            element={
              <ProtectedRoute pageKey="web.admin.home.minerDetails">
                <MinerDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-group"
            element={
              <ProtectedRoute pageKey="web.admin.home.priceGroup">
                <PricesGroup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-providers/overview"
            element={
              <ProtectedRoute pageKey="web.admin.home.paymentProviders.overview">
                <PaymentProvidersOverview />
              </ProtectedRoute>
            }
          />
          {/* route removed (2026-08 frontend audit): /payment-providers/add posted to /gateways, which this backend does not serve */}
          {/* <Route
            path="/fundamentals"
            element={
              <ProtectedRoute pageKey="web.admin.home.fundamentals">
                <Fundamentals />
              </ProtectedRoute>
            }
          /> */}

          {/* Settings */}
          <Route
            path="/settings/branding"
            element={
              <ProtectedRoute pageKey="web.admin.settings.branding">
                <BrandingSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/monitoring"
            element={
              <ProtectedRoute pageKey="web.admin.settings.monitoring">
                <Monitoring />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/parameters"
            element={
              <ProtectedRoute pageKey="web.admin.settings.Parameters">
                <Parameters />
              </ProtectedRoute>
            }
          />

          <Route path="/errors/internal404" element={<Internal404 />} />
          <Route path="/no-permission-page" element={<NoPermissionPage />} />
          <Route path="/jwt" element={<JwtPage />} />
        </Route>

        {/* Catch-all for 404 */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </ProductProvider>
  );
};

export default FalconRoutes;
